import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormInput } from "../components/FormInput";
import { FormInputSearch } from "../components/FormInputSearch";
import FormNumberInput from "../components/FormNumberInput";
import NoInfo from "../components/NoInfo";
import PrimePanel from "../components/PrimePanel";
import NumberUtil from "../utility/NumberUtil";
import { Chip } from "primereact/chip";

const renderHeader = (label, required = false) => <span className={`${required ? "required" : ""}`}>{label}</span>;

const renderFooter = (onClick) => (
  <button type="button" className="icon-btn" onClick={onClick}>
    <i className="pi pi-plus"></i>
  </button>
);

const renderEditor = (type, fieldsId, rowIndex, fieldName, form = {}, options = {}) => {
  const fullName = `${fieldsId}[${rowIndex}].${fieldName}`;

  switch (type) {
    case "dropdown":
      return (
        <Controller
          name={fullName}
          control={form.control}
          rules={form.rules}
          render={({ field, fieldState }) => (
            <>
              <Dropdown
                {...field}
                filter
                disabled={options.disabled ?? false}
                showClear
                className={`w-full ${fieldState.invalid ? "p-invalid" : ""}`}
                options={options.options || []}
                optionLabel={options.optionLabel || "label"}
                optionValue={options.optionValue || "value"}
                placeholder={options.placeholder}
                value={field.value ?? null}
                virtualScrollerOptions={{ itemSize: 40 }}
                onChange={(e) => {
                  const newValue = e.value ?? null;
                  field.onChange(newValue);
                  options?.editorCallback?.(newValue);
                  options?.onChange?.(rowIndex, newValue);
                }}
              />
              {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}
            </>
          )}
        />
      );

    case "multiSelect":
      return (
        <Controller
          name={fullName}
          control={form.control}
          rules={form.rules}
          render={({ field, fieldState }) => (
            <>
              <MultiSelect
                filter
                showClear
                disabled={(options.customDisabled[rowIndex] || options.disabled) ?? false}
                display={options.display || "chip"}
                className={`w-full ${fieldState.invalid ? "p-invalid" : ""}`}
                options={options?.customOptions[rowIndex] ?? options.options ?? []}
                optionLabel={options.optionLabel || "label"}
                optionValue={options.optionValue || "value"}
                value={field.value ?? []}
                placeholder={options.placeholder}
                virtualScrollerOptions={{ itemSize: 40 }}
                onChange={(e) => {
                  const newValue = e.value ?? null;
                  field.onChange(newValue);
                  options?.editorCallback?.(newValue);
                  options?.onChange?.(rowIndex, newValue);
                }}
              ></MultiSelect>
              {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}
            </>
          )}
        />
      );

    case "search":
      return (
        <Controller
          name={fullName}
          control={form.control}
          rules={form.rules}
          render={({ field, fieldState }) => (
            <>
              <FormInputSearch
                disabled={options.disabled ?? false}
                register={form.register}
                errors={form.errors}
                id={fullName}
                name={fullName}
                type="text"
                readOnly={true}
                value={field.value ?? ""}
                onChange={(val) => field.onChange(val)}
                disabledBtn={options.disabledBtn}
                btnSerch={() => {
                  options?.btnSerch?.(rowIndex);
                }}
              />
              {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}
            </>
          )}
        />
      );

    case "text":
      return (
        <Controller
          name={fullName}
          control={form.control}
          rules={form.rules}
          render={({ field, fieldState }) => (
            <>
              <FormInput
                disabled={options.disabled ?? false}
                register={form.register}
                errors={form.errors}
                inputClass={`w-full ${fieldState.invalid ? "p-invalid" : ""}`}
                placeholder={options.placeholder}
                id={fullName}
                name={fullName}
                value={field.value ?? ""}
                onChange={(val) => {
                  field.onChange(val);
                }}
                enterClick={(e) => {
                  inputCheckKeyDown(e);
                }}
              />
              {fieldState.error && <small className="p-error block mt-1">{fieldState.error.message}</small>}
            </>
          )}
        />
      );

    case "number":
      return (
        <Controller
          name={fullName}
          control={form.control}
          rules={form.rules}
          render={({ field, fieldState }) => (
            <>
              <FormNumberInput
                disabled={options.disabled ?? false}
                className={`w-full ${fieldState.invalid ? "p-invalid" : ""}`}
                errors={form.errors}
                placeholder={options.placeholder}
                value={options.value?.(rowIndex) ?? field.value}
                onChange={(val) => {
                  field.onChange(val);
                  options?.editorCallback?.(val);
                  options?.onChange?.(rowIndex, val);
                }}
                enterClick={(e) => {
                  inputCheckKeyDown(e);
                }}
              />
              <div className="flex justify-between mt-1">
                <small className="flex-1 p-error text-left">{fieldState.error?.message}</small>
                <small className="flex-1 text-right">{options?.displayText?.(rowIndex)}</small>
              </div>
            </>
          )}
        />
      );

    default:
      return null;
  }
};

//避免觸發 按enter 後 就觸發 onSubmit 事件冒泡 給input 使用 <FormInput enterClick事件 ; 一般input onKeyDown事件
const inputCheckKeyDown = (e) => {
  if (e.key === "Enter") {
    e.preventDefault();
  }
};

const nonEditableCell = (value, type = "text") => {
  const safeValue = value ?? ""; // null 或 undefined 轉空字串
  const valueDisplay = type === "number" && safeValue ? NumberUtil.formatWithCommas(safeValue) || "" : safeValue;
  return <div className={`non-editable-cell text-ellipsis ${type === "number" ? "right" : ""}`}>{valueDisplay}</div>;
};

const renderDeleteButton = (rowData, actionConfig) => {
  const { fields, fieldId, handleDelForm, handleResetForm, fieldArrayApi, defaultList } = actionConfig;
  const index = fields.findIndex((item) => item[fieldId] === rowData[fieldId]);

  return (
    <button
      type="button"
      className="icon-btn"
      onClick={() => {
        fields.length === 1
          ? handleResetForm(fieldArrayApi, defaultList, index)
          : handleDelForm(fieldArrayApi, defaultList, index, fields.length);
      }}
    >
      <i className="pi pi-minus"></i>
    </button>
  );
};

const renderEditorBody = (type, rowData, fieldName, options = {}) => {
  switch (type) {
    case "dropdown": {
      const { options: list = [], optionLabel = "label", optionValue = "value" } = options;
      const selected = list.find((item) => item?.[optionValue] === rowData?.[fieldName]);
      return selected ? selected[optionLabel] : "";
    }
    case "search":
      return rowData[options.fieldBodyName ?? fieldName];
    case "multiSelect": {
      const selectedValues = rowData?.[fieldName] ?? [];
      const optionsList = options?.customOptions?.[options?.rowIndex] ?? options?.options ?? [];

      const selectedLabels = optionsList
        .filter((item) => selectedValues.includes(item[options.optionValue || "value"]))
        .map((item) => item[options.optionLabel || "label"]);

      return (
        <div className="flex flex-wrap gap-2">
          {selectedLabels.map((label, idx) => (
            <Chip
              key={idx}
              label={label}
              style={{
                backgroundColor: "var(--primary-color)",
                color: "white",
                fontSize: "14px",
                height: "21px",
                padding: "0 10px",
              }}
            />
          ))}
        </div>
      );
    }
    case "number":
      return <div className="text-right">{rowData[fieldName]}</div>;
    default:
      return rowData[fieldName];
  }
};

const PanelDataTable = ({ panelConfig = {}, dataTableConfig = {}, columnsConfig = [], actionConfig = {} }) => {
  const { t } = useTranslation();
  if (!panelConfig || !panelConfig.header) return null;

  return (
    <PrimePanel
      toggleable
      className={`${panelConfig.className} dataTable-panel evaluation-temp-setting-panel`}
      header={panelConfig.header}
      headerTemplate={panelConfig.headerTemplate}
      // 透過 panelConfig 傳遞初始收合狀態/切換事件
      collapsed={panelConfig.collapsed}
      onToggle={panelConfig.onToggle}
    >
      {dataTableConfig.value && (
        <DataTable
          scrollable
          scrollHeight="35rem"
          stripedRows
          resizableColumns
          editMode="cell"
          dataKey={dataTableConfig.dataKey}
          className={`${dataTableConfig.className} white-data-table hover-highlight no-shadow`}
          value={dataTableConfig.value}
          emptyMessage={<NoInfo panel="table" />}
          {...(dataTableConfig.value.length > 20
            ? {
              virtualScrollerOptions: { itemSize: 79 },
            }
            : {})}
          {...(dataTableConfig.footer ? { footer: dataTableConfig.footer } : {})}
          {...(dataTableConfig.footerEvent && !dataTableConfig.footerDisabled
            ? { footer: renderFooter(dataTableConfig.footerEvent) }
            : {})}
        >
          {(dataTableConfig.item?.display ?? true) && (
            <Column
              header={t("itemNo")}
              frozen={dataTableConfig?.item?.frozen ?? true}
              alignFrozen={dataTableConfig?.item?.alignFrozen ?? "left"}
              style={{
                minWidth: dataTableConfig?.item?.minWidth || "50px",
                width: dataTableConfig?.item?.width || "auto",
                maxWidth: dataTableConfig?.item?.maxWidth || "auto",
              }}
              body={(rowData, { rowIndex }) => rowIndex + 1}
            />
          )}
          {columnsConfig.length > 0 &&
            columnsConfig.map((col) => (
              <Column
                key={col.field}
                {...(col.frozen ? { frozen: col.frozen } : {})}
                {...(col.frozen && col.alignFrozen ? { alignFrozen: col.alignFrozen ?? "left" } : {})}
                style={{
                  minWidth: col.minWidth || "180px",
                  width: col.width || "auto",
                  maxWidth: col.maxWidth || "auto",
                }}
                field={col.field}
                header={renderHeader(col.header, col.headerRequired)}
                headerClassName={`${col.type === "number" ? "right-header" : ""}`}
                body={(rowData, { rowIndex }) =>
                  (rowData?.disabledColumns?.includes(col.field) || col.nonEditor?.(rowData, rowIndex)) ?? false
                    ? nonEditableCell(col.displayText?.(rowData, rowIndex) ?? rowData[col.field], col.type)
                    : col.disabled
                      ? renderEditorBody(col.type, rowData, col.field, {
                        options: col.options,
                        optionLabel: col.optionLabel,
                        optionValue: col.optionValue,
                        customOptions: col.customOptions,
                        rowIndex: rowIndex,
                        fieldBodyName: col.fieldBodyName,
                      })
                      : renderEditor(
                        col.type,
                        col.fieldListName,
                        rowIndex,
                        col.fieldBodyName ?? col.fieldName,
                        {
                          control: col.control,
                          rules: typeof col.rules === "function" ? col.rules(rowIndex) : col.rules,
                          register: col.register,
                          errors: col.errors,
                        },
                        {
                          placeholder: col.placeholder,
                          options: col.options,
                          optionLabel: col.optionLabel,
                          optionValue: col.optionValue,
                          customOptions: col.customOptions,
                          disabled: col.disabled,
                          customDisabled: col.customDisabled,
                          btnSerch: col.btnSerch,
                          enterClick: col.enterClick,
                          onChange: col.onChange,
                          value: col.value,
                          disabledBtn: col.disabledBtn,
                          displayText: col.displayText,
                        },
                      )
                }
              />
            ))}

          {/* 動作 */}
          {actionConfig.fields && !actionConfig.disabled && (
            <Column
              frozen={actionConfig.frozen ?? true}
              alignFrozen={actionConfig.alignFrozen ?? "right"}
              style={{ width: "1.5rem" }}
              body={(rowData) => renderDeleteButton(rowData, actionConfig)}
            />
          )}
        </DataTable>
      )}
    </PrimePanel>
  );
};

export default PanelDataTable;
